import type { ScanOutcome, ScanSyncItem } from "./scan-contract";
import type { SubmitScanResult } from "./submit-scan";

// Offline scan queue core for Guard Web — pure, storage-agnostic, and deliberately small. Mirrors
// the native app's queue semantics (guard-core/mobile local-scan-queue) so web capture has the same
// field-reliability posture: a scan is never lost because the network dropped.
//
//   queued ──beginSync──▶ syncing ──completeSync──▶ synced (validated/flagged/duplicate)
//     ▲                      │                  └──▶ rejected   (server said no — terminal)
//     └──────failSync────────┘   (transport failure → back to queued, retryCount+1)
//
// Idempotency (ADR-004/ADR-005): the item's clientEventId is stamped ONCE at enqueue and never
// changes across retries — the server treats (account_id, clientEventId) as unique, so replaying
// after a timeout can never create a duplicate.
//
// All transition helpers are immutable (return new arrays) so React state + persistence stay
// trivially correct. Persistence goes through the QueueStore seam; localStorage is the default
// driver (small queues, synchronous, resilient), swappable for IndexedDB later without touching
// queue logic.

export type QueuedScanState = "queued" | "syncing" | "synced" | "rejected";

export interface QueuedScanRecord {
  /** The immutable sync item; item.clientEventId is the queue key + server idempotency key. */
  item: ScanSyncItem;
  state: QueuedScanState;
  enqueuedAt: string;
  retryCount: number;
  lastError: string | null;
  /** Server outcome once synced/rejected (validated | flagged | duplicate | rejected), else null. */
  outcome: ScanOutcome | null;
  /** Server-resolved human checkpoint label once known. Never a token/UUID. */
  checkpointName: string | null;
}

/** Append a scan to the queue (FIFO). Re-enqueueing the same clientEventId is a no-op. */
export function enqueueScan(
  records: readonly QueuedScanRecord[],
  item: ScanSyncItem,
  enqueuedAt: string,
): QueuedScanRecord[] {
  if (records.some((record) => record.item.clientEventId === item.clientEventId)) {
    return [...records];
  }
  return [
    ...records,
    {
      item,
      state: "queued",
      enqueuedAt,
      retryCount: 0,
      lastError: null,
      outcome: null,
      checkpointName: null,
    },
  ];
}

/** First record still waiting to upload, in FIFO order. */
export function nextQueued(records: readonly QueuedScanRecord[]): QueuedScanRecord | null {
  return records.find((record) => record.state === "queued") ?? null;
}

export function beginSync(
  records: readonly QueuedScanRecord[],
  clientEventId: string,
): QueuedScanRecord[] {
  return records.map((record) =>
    record.item.clientEventId === clientEventId && record.state === "queued"
      ? { ...record, state: "syncing" as const }
      : record,
  );
}

/** Server accepted the upload. "rejected" is terminal-rejected; everything else is synced. */
export function completeSync(
  records: readonly QueuedScanRecord[],
  clientEventId: string,
  result: { outcome: ScanOutcome; checkpointName: string | null },
): QueuedScanRecord[] {
  return records.map((record) =>
    record.item.clientEventId === clientEventId
      ? {
          ...record,
          state: result.outcome === "rejected" ? ("rejected" as const) : ("synced" as const),
          outcome: result.outcome,
          checkpointName: result.checkpointName,
          lastError: null,
        }
      : record,
  );
}

/** Transport failure — the scan stays QUEUED (never dropped) and will retry with the SAME id. */
export function failSync(
  records: readonly QueuedScanRecord[],
  clientEventId: string,
  error: string,
): QueuedScanRecord[] {
  return records.map((record) =>
    record.item.clientEventId === clientEventId
      ? {
          ...record,
          state: "queued" as const,
          retryCount: record.retryCount + 1,
          lastError: error,
        }
      : record,
  );
}

export interface QueueCounts {
  queued: number;
  syncing: number;
  synced: number;
  rejected: number;
  total: number;
}

export function queueCounts(records: readonly QueuedScanRecord[]): QueueCounts {
  const counts: QueueCounts = { queued: 0, syncing: 0, synced: 0, rejected: 0, total: records.length };
  for (const record of records) {
    counts[record.state] += 1;
  }
  return counts;
}

/** Keep the persisted queue small: retain all pending records but only the newest `keep` terminal
 *  (synced/rejected) ones — mirrors mobile's terminal purge. */
export function pruneTerminal(
  records: readonly QueuedScanRecord[],
  keep: number = 20,
): QueuedScanRecord[] {
  const terminal = records.filter((record) => record.state === "synced" || record.state === "rejected");
  const drop = new Set(terminal.slice(0, Math.max(0, terminal.length - keep)));
  return records.filter((record) => !drop.has(record));
}

export interface ReplaySummary {
  records: QueuedScanRecord[];
  synced: number;
  rejected: number;
  failed: number;
}

/**
 * Upload every queued record in FIFO order. Each record is attempted once per replay: server
 * acceptance → synced/rejected (terminal); transport failure → back to queued (retry later, same
 * clientEventId). `persist` is called after every transition so a crash mid-replay never loses
 * state. Sequential by design — order preservation beats parallel upload for patrol evidence.
 */
export async function replayQueue(
  initial: readonly QueuedScanRecord[],
  submit: (item: ScanSyncItem) => Promise<SubmitScanResult>,
  persist: (records: readonly QueuedScanRecord[]) => void = () => {},
): Promise<ReplaySummary> {
  let records = [...initial];
  let synced = 0;
  let rejected = 0;
  let failed = 0;

  for (;;) {
    const next = nextQueued(records);
    if (next === null) {
      break;
    }
    const id = next.item.clientEventId;
    records = beginSync(records, id);
    persist(records);

    const result = await submit(next.item);
    if (result.ok) {
      records = completeSync(records, id, {
        outcome: result.outcome,
        checkpointName: result.checkpointName,
      });
      if (result.outcome === "rejected") {
        rejected += 1;
      } else {
        synced += 1;
      }
    } else {
      records = failSync(records, id, result.reason);
      failed += 1;
    }
    persist(records);

    // A transport failure usually means we're (still) offline — stop this replay pass; the
    // remaining records stay queued untouched and the next reconnect/flush retries from the front.
    if (!result.ok) {
      break;
    }
  }

  return { records, synced, rejected, failed };
}

// --- Storage seam -------------------------------------------------------

export interface QueueStore {
  load(): QueuedScanRecord[];
  save(records: readonly QueuedScanRecord[]): void;
}

const QUEUE_STORAGE_KEY = "secops.guard.scanQueue";

interface StringStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** In-memory store — tests + private-mode fallback. */
export function createMemoryQueueStore(): QueueStore {
  let snapshot: QueuedScanRecord[] = [];
  return {
    load: () => [...snapshot],
    save: (records) => {
      snapshot = [...records];
    },
  };
}

/**
 * localStorage-backed store. Corrupted/missing data loads as an empty queue (never throws into the
 * app); failed writes are swallowed (the in-memory queue remains authoritative for the session).
 */
export function createLocalStorageQueueStore(
  storage?: StringStorageLike,
  storageKey: string = QUEUE_STORAGE_KEY,
): QueueStore {
  const target = storage ?? defaultStorage();
  if (!target) {
    return createMemoryQueueStore();
  }
  return {
    load: () => {
      try {
        const raw = target.getItem(storageKey);
        if (raw === null) {
          return [];
        }
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
          return [];
        }
        return parsed.flatMap(normalizePersistedRecord);
      } catch {
        return [];
      }
    },
    save: (records) => {
      try {
        target.setItem(storageKey, JSON.stringify(records));
      } catch {
        // Quota/private-mode write failure — session keeps its in-memory queue.
      }
    },
  };
}

function defaultStorage(): StringStorageLike | undefined {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // Sandboxed/blocked storage access throws.
  }
  return undefined;
}

function isQueuedScanRecord(value: unknown): value is QueuedScanRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  const item = record.item as Record<string, unknown> | undefined;
  return (
    typeof item === "object" &&
    item !== null &&
    typeof item.clientEventId === "string" &&
    (record.state === "queued" ||
      record.state === "syncing" ||
      record.state === "synced" ||
      record.state === "rejected")
  );
}

function normalizePersistedRecord(value: unknown): QueuedScanRecord[] {
  if (!isQueuedScanRecord(value)) {
    return [];
  }
  if (value.state !== "syncing") {
    return [value];
  }
  return [
    {
      ...value,
      state: "queued",
      lastError: value.lastError ?? "interrupted sync recovered",
    },
  ];
}

export { QUEUE_STORAGE_KEY };
